/**
 * LayerSharePopover
 *
 * Share-icon button + top-right modal presenting the export options as the 2x2
 * matrix locked in the GeoStyler/SDW review (§10, §10.1) — not a vertical list
 * of buttons, which hid how the options relate:
 *
 *                    Offline                      Live
 *                (you keep the file)       (CultureMap over HTTPS)
 *   Raw          Download original /       Copy data URL
 *   (data only)  Download GeoJSON          same link the live .qlr uses
 *
 *   Styled       QGIS style file           QGIS live layer
 *                .qml named for the Raw    .qlr + signed web URL
 *                file; QGIS applies it
 *
 * Styled Offline is always one generated .qml; it never fetches data. See
 * docs/developer-guides/llm-context/2026-09-29_frontend-share-offline-one-model.md.
 *
 * "Copy data URL" is not a fourth product — it is the live .qlr's datasource
 * unwrapped, so it shares the Live column's lifetime, auth and grey-out rules.
 *
 * Unavailable cells stay VISIBLE and greyed with a one-line reason rather than
 * being hidden, so the shape of the offering is always legible.
 *
 * Live files carry their own ~8 h credential — a `?dl_token=` for CM-tables, a
 * CloudFront-signed URL for spatial datasets — so there is nothing for the user
 * to configure. API keys don't work for QGIS; a layer that doesn't expire comes
 * from the CultureMap QGIS plugin. See
 * docs/developer-guides/llm-context/2026-09-24_backend-to-frontend-qgis-sharing.md.
 */

import React, { useEffect, useMemo, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { useSnackbar } from "notistack";
import ShareIcon from "@mui/icons-material/Share";
import type { Style } from "geostyler-style";
import { isAxiosError } from "axios";

import { ApiEndpoint, SpatialDatasetType } from "../../types";
import { cmTableExportBasename, getCmTableSlugFromEndpoint } from "../../utilities/cmTableEndpoint";
import { saveBlob, startBrowserDownload } from "../../utilities/browserDownload";
import { tableStyleToGeostyler } from "../../utilities/legacyStyleToGeostyler";
import {
  buildCmTableQlr,
  buildRemoteVectorQlr,
  buildRasterCogQlr,
  buildQmlStyle,
  buildVectorQml,
  downloadQlrFile,
  downloadQmlFile,
  qmlFilenameFor,
  QgisGeometryType
} from "../../utilities/buildQlrFile";
import { geostylerToQgisPaintedRasterPipe, geostylerToQgisRasterPipe } from "../../utilities/geostylerToQgis";
import { fetchQgisIcons } from "../../utilities/qgisIcons";
import { findRasterSymbolizer, spatialDatasetStyleToGeostyler, type SpatialGeometryType } from "../../utilities/spatialDatasetStyle";
import { baseGeometry } from "../../utilities/cmTableStyle";
import type { SpatialDatasetQgisExportResponse } from "../../services/spatialDatasetService";
import { useAuthStore } from "../../store/auth";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Shape for CM-table share context */
export interface CmTableShareContext {
  kind: "cm-table";
  endpoint: ApiEndpoint;
  /** Optional record scope for exports opened from a record detail page. */
  rowIds?: number[];
  /** Human-readable record name, used in the share dialog. */
  rowLabel?: string;
}

/** Shape for spatial-dataset share context */
export interface SpatialDatasetShareContext {
  kind: "spatial-dataset";
  datasetId: number;
  datasetName: string;
  /**
   * Known synchronously from the caller (which already has the dataset), so
   * the Offline/Styled cell's raster-vs-vector branch (§P3-12) doesn't have to
   * wait on — or silently mis-render while — the async `qgis-export` fetch.
   */
  datasetType?: SpatialDatasetType;
}

export type LayerShareContext = CmTableShareContext | SpatialDatasetShareContext;

interface LayerSharePopoverProps {
  /** The share context — determines which API path to use */
  shareContext: LayerShareContext;
  /** Size for the trigger IconButton */
  size?: "small" | "medium";
  /** Icon button sx overrides (for colour matching the action bar) */
  iconSx?: object;
}

/**
 * Whole-table exports are routinely megabytes; the 10 s axios default aborts
 * them and surfaces as a generic error with no hint that size was the cause.
 * Only the two offline handlers pull the full document, so only they raise it.
 */
const EXPORT_FETCH_TIMEOUT_MS = 120_000;

/** Derives the renderer-neutral GeoStyler style from a CM-table endpoint's stored style (legacy or already-migrated). */
function cmTableGeostylerStyle(endpoint: ApiEndpoint): Style {
  const rawStyle = endpoint.style as Record<string, unknown> | undefined;
  if (rawStyle && Array.isArray((rawStyle as { rules?: unknown }).rules)) return rawStyle as unknown as Style;
  if (!rawStyle) return { name: getCmTableSlugFromEndpoint(endpoint) ?? "layer", rules: [] } as unknown as Style;
  return tableStyleToGeostyler(
    rawStyle as Parameters<typeof tableStyleToGeostyler>[0],
    endpoint.spatialType ?? "point"
  ) as unknown as Style;
}

/** The QGIS geometry of a CM-table. */
function cmTableQgisGeometry(endpoint: ApiEndpoint): QgisGeometryType {
  const geometry = baseGeometry(endpoint.spatialType);
  return geometry === "linestring" ? "line" : geometry === "polygon" ? "polygon" : "point";
}

/**
 * The dataset's style in current (GeoStyler) form, with old flat-format styles
 * upgraded exactly as the web map reads them. Every QGIS export uses this —
 * raster styles in particular were never migrated server-side, so
 * `{"raster-opacity": 0.8}` flat styles are still stored.
 */
function datasetGeostylerStyle(info: SpatialDatasetQgisExportResponse, name = "layer"): Style {
  const geometry: SpatialGeometryType = info.dataset_type === "raster" ? "raster" : (info.geometry_type ?? "point");
  return spatialDatasetStyleToGeostyler(info.display_style, geometry, name);
}

/**
 * Mints a CM-table download token and returns its `?dl_token=` GeoJSON URL,
 * narrowed to `rowIds` for a record export. The URL goes into the `.qlr` bare —
 * no `/vsicurl/` (see `buildCmTableQlr`).
 */
async function resolveCmTableDatasource(slug: string, rowIds: number[] | undefined): Promise<string> {
  const { createCmTableDownloadToken, withRowIds } = await import("../../services/cmTables");
  const res = await createCmTableDownloadToken(slug);
  return withRowIds(res.data.url, rowIds);
}

type VectorSource = "geoparquet" | "gpkg";

/**
 * The signed `/vsicurl/` datasource for a vector's live layer: the user's
 * `choice` when qgis-export offers it, otherwise the artifact qgis-export
 * picked. GPKG is a container, so its `.qlr` datasource also needs
 * `|layername=layer` — the caller adds that, since a copied URL must not carry it.
 */
function vectorLiveSource(info: SpatialDatasetQgisExportResponse, choice: VectorSource | null): { datasource: string; isGpkg: boolean } {
  const picked = choice ? info.qgis_vector_sources?.[choice] : undefined;
  if (picked) return { datasource: picked, isGpkg: choice === "gpkg" };
  if (!info.qgis?.datasource) throw new Error(info.qgis?.reason ?? "This dataset has no QGIS-compatible source yet — use Offline.");
  return { datasource: info.qgis.datasource, isGpkg: info.qgis.artifact === "gpkg" };
}

// ─── Component ────────────────────────────────────────────────────────────────

const LayerSharePopover: React.FC<LayerSharePopoverProps> = ({ shareContext, size = "medium", iconSx }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);

  // Per-action loading states
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingRaw, setIsDownloadingRaw] = useState(false);
  const [isDownloadingStyle, setIsDownloadingStyle] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [copied, setCopied] = useState(false);

  // Whether the Live column is usable has to be known at *render* time to grey
  // the cells, so the qgis-export metadata is fetched when the modal opens
  // rather than on click. Handlers reuse this response instead of re-fetching.
  const [exportInfo, setExportInfo] = useState<SpatialDatasetQgisExportResponse | null>(null);
  const [probingLive, setProbingLive] = useState(false);

  const datasetId = shareContext.kind === "spatial-dataset" ? shareContext.datasetId : null;

  useEffect(() => {
    if (!open || datasetId === null) return;
    let cancelled = false;
    setProbingLive(true);
    void (async () => {
      try {
        const { getSpatialDatasetQgisExport } = await import("../../services/spatialDatasetService");
        const res = await getSpatialDatasetQgisExport(datasetId);
        if (!cancelled) setExportInfo(res);
      } catch (err) {
        // Not fatal: the Offline column still works. Leaving exportInfo null
        // greys the Live column, which is the safe direction to fail in.
        console.warn("[LayerShare] could not resolve QGIS export metadata", err);
        if (!cancelled) setExportInfo(null);
      } finally {
        if (!cancelled) setProbingLive(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, datasetId]);

  // Official macOS QGIS ships no Parquet driver, so when qgis-export signs both
  // a GeoParquet and a GPKG, the user can pick GPKG. Null = qgis-export's pick.
  const [vectorSource, setVectorSource] = useState<VectorSource | null>(null);
  const vectorSources = exportInfo?.qgis_vector_sources ?? {};
  const offerGpkg = Boolean(vectorSources.geoparquet && vectorSources.gpkg);
  const effectiveVectorSource: VectorSource = vectorSource ?? (exportInfo?.qgis?.artifact === "gpkg" ? "gpkg" : "geoparquet");

  // A download token reads data as the person who minted it, and a superuser
  // holds no organisation, so the backend refuses to mint one (400). Only the
  // CM-table Live cells mint a token; Offline goes through the app's session.
  const isSuperuser = useAuthStore((s) => Boolean(s.user?.is_superuser));

  /**
   * Live column availability. The live `.qlr` targets the analysis artifact
   * (COG for rasters, GeoParquet/GPKG for vectors), so a raster whose *display*
   * format is PMTiles still has one. What greys the column:
   *
   *   - CM-table, superuser — the backend won't mint them a download token.
   *   - `analysis_type === null` — converted before the analysis artifact
   *     existed. An admin needs to re-process it (an operator action).
   *   - `qgis.supported === false` — no signed source to hand QGIS; the
   *     backend's `reason` says why.
   */
  const live = useMemo<{ ok: boolean; reason: string | null }>(() => {
    if (shareContext.kind === "cm-table") {
      return isSuperuser
        ? { ok: false, reason: "Superusers hold no organisation — sign in as a user in this organisation for a live link." }
        : { ok: true, reason: null };
    }
    if (probingLive) return { ok: false, reason: null };
    if (!exportInfo) return { ok: false, reason: "Live link unavailable — the dataset may still be processing." };

    if (exportInfo.analysis_type === null) {
      return { ok: false, reason: "Live layers are not available for this dataset yet — an admin can re-process it. Use Offline." };
    }

    if (!exportInfo.qgis?.supported) {
      return { ok: false, reason: exportInfo.qgis?.reason ?? "No live source for this dataset yet — use Offline." };
    }

    return { ok: true, reason: null };
  }, [shareContext.kind, exportInfo, probingLive, isSuperuser]);

  // ─── Resolve qgis-export metadata ───────────────────────────────────────────

  /** Reuses the modal-open fetch when available rather than paying for a second round-trip. */
  async function resolveQgisExport(): Promise<SpatialDatasetQgisExportResponse> {
    if (exportInfo) return exportInfo;
    const { getSpatialDatasetQgisExport } = await import("../../services/spatialDatasetService");
    if (shareContext.kind !== "spatial-dataset") throw new Error("Not a spatial dataset.");
    return getSpatialDatasetQgisExport(shareContext.datasetId);
  }

  // ─── Download .qlr (live, styled) ───────────────────────────────────────────
  //
  // The live `.qlr` always targets the ANALYSIS artifact now — a COG for
  // rasters, GeoParquet (or GPKG fallback) for vectors — never the display
  // file: QGIS can't use raster PMTiles (no GDAL driver) or vector tiles
  // (clipped/simplified, no valid provider) as a working layer. Branches on
  // `dataset_type`, not `display_type`, because a raster's *display* format
  // (PMTiles or COG) no longer decides whether its *live* layer works — every
  // raster with an analysis artifact has one.

  async function handleDownloadQlr() {
    setIsDownloading(true);
    try {
      if (shareContext.kind === "cm-table") {
        const { endpoint, rowIds } = shareContext;
        const slug = getCmTableSlugFromEndpoint(endpoint);
        if (!slug) throw new Error("Cannot determine table slug.");
        const layerName = endpoint.name ?? slug;

        const style = cmTableGeostylerStyle(endpoint);
        const [datasource, icons] = await Promise.all([resolveCmTableDatasource(slug, rowIds), fetchQgisIcons(style)]);
        const qlrXml = buildCmTableQlr(slug, layerName, datasource, style, cmTableQgisGeometry(endpoint), { icons });
        downloadQlrFile(cmTableExportBasename(endpoint, rowIds), qlrXml);
      } else {
        const info = await resolveQgisExport();
        const { datasetName } = shareContext;

        // Read the CRS from the response rather than assuming it: rasters are
        // reprojected to EPSG:3857, vectors stay in EPSG:4326.
        const crs = info.qgis?.crs ?? info.display_crs;

        if (info.dataset_type === "raster") {
          if (!info.qgis?.datasource) throw new Error(info.qgis?.reason ?? "This dataset has no QGIS-compatible source yet.");
          const symbolizer = findRasterSymbolizer(datasetGeostylerStyle(info, datasetName));
          // `qgis_styled` is the same signed COG with the web map's styling painted
          // into its pixels (hue and extreme sliders included), so the .qlr adds
          // only smoothing. Otherwise the plain COG gets a pipe whose renderer names
          // the alpha band from `raster`, or is left out (geostylerToQgisRasterPipe).
          const styled = info.qgis_styled?.supported ? info.qgis_styled.datasource : null;
          const qlrXml = styled
            ? buildRasterCogQlr(datasetName, styled, crs, geostylerToQgisPaintedRasterPipe(symbolizer))
            : buildRasterCogQlr(
                datasetName,
                info.qgis.datasource,
                crs,
                geostylerToQgisRasterPipe(symbolizer, { target: "live", bands: info.bands, raster: info.raster })
              );
          downloadQlrFile(datasetName, qlrXml);
        } else if (info.dataset_type === "vector") {
          const { datasource, isGpkg } = vectorLiveSource(info, vectorSource);
          const style = datasetGeostylerStyle(info, datasetName);
          const qlrXml = buildRemoteVectorQlr(
            datasetName,
            isGpkg ? `${datasource}|layername=layer` : datasource,
            style,
            info.geometry_type ?? "point",
            crs,
            { icons: await fetchQgisIcons(style) }
          );
          downloadQlrFile(datasetName, qlrXml);
        } else {
          throw new Error("Tabular datasets have no live map layer.");
        }
      }

      setOpen(false);
    } catch (err) {
      console.error("[LayerShare] download failed", err);
      const stillProcessing = isAxiosError(err) && err.response?.status === 400 && shareContext.kind === "spatial-dataset";
      enqueueSnackbar(
        stillProcessing
          ? "This dataset is still processing. Try again once conversion completes."
          : err instanceof Error
            ? err.message
            : "Could not generate layer file — please try again.",
        {
          variant: "error"
        }
      );
    } finally {
      setIsDownloading(false);
    }
  }

  // ─── Offline · Raw ──────────────────────────────────────────────────────────

  /** A dataset's original: S3 names the file, so the browser saves it natively. */
  async function handleDownloadOriginal() {
    if (shareContext.kind !== "spatial-dataset") return;
    setIsDownloadingRaw(true);
    try {
      const { getSpatialDatasetDownloadUrl } = await import("../../services/spatialDatasetService");
      const download = await getSpatialDatasetDownloadUrl(shareContext.datasetId);
      if (!download?.url) throw new Error("No download URL returned.");
      startBrowserDownload(download.url);
      setOpen(false);
    } catch (err) {
      console.error("[LayerShare] original download failed", err);
      enqueueSnackbar("Could not retrieve original file — please try again.", { variant: "error" });
    } finally {
      setIsDownloadingRaw(false);
    }
  }

  /**
   * A CM-table's GeoJSON, fetched with the app's login (a link can't send one)
   * and saved exactly as the server sent it.
   */
  async function handleDownloadCmTableGeojson() {
    if (shareContext.kind !== "cm-table") return;
    setIsDownloadingRaw(true);
    try {
      const { endpoint, rowIds } = shareContext;
      const slug = getCmTableSlugFromEndpoint(endpoint);
      if (!slug) throw new Error("Cannot determine table slug.");
      const { getCmTableGeojsonFile } = await import("../../services/cmTables");
      const file = await getCmTableGeojsonFile(slug, { rowIds, timeout: EXPORT_FETCH_TIMEOUT_MS });
      saveBlob(`${cmTableExportBasename(endpoint, rowIds)}.geojson`, file);
      setOpen(false);
    } catch (err) {
      console.error("[LayerShare] GeoJSON download failed", err);
      enqueueSnackbar("Could not download GeoJSON — please try again.", { variant: "error" });
    } finally {
      setIsDownloadingRaw(false);
    }
  }

  // ─── Copy link ─────────────────────────────────────────────────────────────

  // "Copy data URL" is the live .qlr's datasource unwrapped (§10) — same
  // lifetime, same credential, same grey-out rules, not a fourth product.
  async function handleCopyLink() {
    setIsCopying(true);
    try {
      let copyText: string;

      if (shareContext.kind === "cm-table") {
        const { endpoint, rowIds } = shareContext;
        const slug = getCmTableSlugFromEndpoint(endpoint);
        if (!slug) throw new Error("Cannot determine table slug.");
        copyText = await resolveCmTableDatasource(slug, rowIds);
      } else {
        const info = await resolveQgisExport();
        let datasource: string;
        if (info.dataset_type === "raster") {
          if (!info.qgis?.datasource) throw new Error(info.qgis?.reason ?? "No live link available for this raster — use Offline.");
          datasource = info.qgis.datasource;
        } else if (info.dataset_type === "vector") {
          // No `|layername=` here — that's a QGIS datasource convention, not part of the URL.
          datasource = vectorLiveSource(info, vectorSource).datasource;
        } else {
          throw new Error("Tabular datasets have no live map layer.");
        }
        copyText = datasource.replace(/^\/vsicurl\//, "");
      }

      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      enqueueSnackbar("Link copied to clipboard.", { variant: "success" });
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error("[LayerShare] copy link failed", err);
      enqueueSnackbar(err instanceof Error ? err.message : "Could not copy link — please try again.", {
        variant: "error"
      });
    } finally {
      setIsCopying(false);
    }
  }

  // ─── Offline · Styled ───────────────────────────────────────────────────────
  // Always one small `.qml`, named so that QGIS applies it to the Raw cell's file
  // when both are in one folder (qmlFilenameFor). Built here; no data is fetched.

  async function handleDownloadStyleFile() {
    setIsDownloadingStyle(true);
    try {
      let dataName: string;
      let qmlXml: string;

      if (shareContext.kind === "cm-table") {
        const { endpoint, rowIds } = shareContext;
        const style = cmTableGeostylerStyle(endpoint);
        dataName = `${cmTableExportBasename(endpoint, rowIds)}.geojson`;
        qmlXml = buildVectorQml(style, cmTableQgisGeometry(endpoint), { icons: await fetchQgisIcons(style) });
      } else {
        const { getSpatialDatasetDownloadUrl } = await import("../../services/spatialDatasetService");
        // Only the filename is used: it is the name the Raw cell's download saves under.
        const [download, info] = await Promise.all([getSpatialDatasetDownloadUrl(shareContext.datasetId), resolveQgisExport()]);
        if (!download?.filename) throw new Error("No download URL returned.");
        dataName = download.filename;
        const style = datasetGeostylerStyle(info, shareContext.datasetName);

        if (info.dataset_type === "raster") {
          // The style lands on the original, which only `raster.source` describes —
          // `bands` is the COG, so it is not passed.
          const pipe = geostylerToQgisRasterPipe(findRasterSymbolizer(style), { target: "offline", raster: info.raster });
          if (!pipe) throw new Error("This raster has no styling QGIS can carry — use Download original.");
          qmlXml = buildQmlStyle(pipe);
        } else if (info.dataset_type === "vector") {
          qmlXml = buildVectorQml(style, info.geometry_type ?? "point", { icons: await fetchQgisIcons(style) });
        } else {
          throw new Error("Tabular datasets have no map style.");
        }
      }

      const qmlName = qmlFilenameFor(dataName);
      downloadQmlFile(qmlName, qmlXml);
      enqueueSnackbar(
        `Keep "${qmlName}" in the same folder as "${dataName}", then drag "${dataName}" into QGIS. Its style loads automatically.`,
        {
          variant: "info"
        }
      );
      setOpen(false);
    } catch (err) {
      console.error("[LayerShare] style file failed", err);
      enqueueSnackbar(err instanceof Error ? err.message : "Could not build the QGIS style file — please try again.", { variant: "error" });
    } finally {
      setIsDownloadingStyle(false);
    }
  }

  // ─── Matrix cell descriptors ────────────────────────────────────────────────
  // The two Offline cells differ by context: a spatial dataset has an uploaded
  // source file, a CM-table has rows. Its raw offline artifact is the GeoJSON;
  // its styled offline artifact is the GeoJSON + SLD zip.

  const busy = isDownloading || isDownloadingRaw || isDownloadingStyle || isCopying;

  const rawOffline =
    shareContext.kind === "spatial-dataset"
      ? { label: "Download original", description: "Original file — no styling", onClick: () => void handleDownloadOriginal() }
      : { label: "Download GeoJSON", description: "Data only — no styling", onClick: () => void handleDownloadCmTableGeojson() };

  const isRasterDataset = shareContext.kind === "spatial-dataset" && (shareContext.datasetType ?? exportInfo?.dataset_type) === "raster";

  // A raster's style file is offered when the style has something QGIS can carry
  // onto the original (see geostylerToQgisRasterPipe) — the same options as the
  // click, so the label matches what it downloads. Otherwise
  // the cell offers the original: an honest label rather than an empty or
  // harmful style file.
  const rasterHasQgisStyle = useMemo(
    () =>
      isRasterDataset &&
      !!exportInfo &&
      geostylerToQgisRasterPipe(findRasterSymbolizer(datasetGeostylerStyle(exportInfo)), {
        target: "offline",
        raster: exportInfo.raster
      }) !== "",
    [isRasterDataset, exportInfo]
  );

  // Every Styled cell is the same product: a .qml for the Raw cell's file.
  const styleFile = (description: string, disabledReason: string | null = null) => ({
    label: "QGIS style file",
    description,
    onClick: () => void handleDownloadStyleFile(),
    disabledReason
  });

  const styledOffline =
    shareContext.kind === "cm-table"
      ? styleFile("Save next to the GeoJSON — QGIS applies it")
      : isRasterDataset
        ? rasterHasQgisStyle
          ? styleFile("Save next to the original — QGIS applies it")
          : // Nothing QGIS can carry: offer the file itself rather than an empty style.
            {
              label: "Download original",
              description: "Original GeoTIFF — no styling",
              onClick: () => void handleDownloadOriginal(),
              disabledReason: null
            }
        : styleFile(
            "Save next to the original — QGIS applies it",
            // The style comes from qgis-export. A click during the probe waits for it (resolveQgisExport).
            !probingLive && !exportInfo ? "Style unavailable — the dataset may still be processing. Use Download original." : null
          );

  // ─── Expiry notice copy (§P3-5) ─────────────────────────────────────────────
  //
  // The honest lifetime of the signed URL / dl_token the live file embeds.

  const expiryNotice = useMemo(() => {
    const seconds = shareContext.kind === "spatial-dataset" ? (exportInfo?.expires_in ?? 28800) : 28800;
    return `Live links expire in ~${Math.round(seconds / 3600)} h.`;
  }, [exportInfo, shareContext.kind]);

  const isRecordExport = shareContext.kind === "cm-table" && Boolean(shareContext.rowIds?.length);
  const displayName = shareContext.kind === "cm-table" ? (shareContext.rowLabel ?? shareContext.endpoint.name) : shareContext.datasetName;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Tooltip title={isRecordExport ? "Share record" : "Share layer"}>
        <IconButton
          size={size}
          onClick={() => setOpen(true)}
          sx={{
            color: "#FCFCFD",
            bgcolor: "transparent",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
            ...iconSx
          }}
          aria-label={isRecordExport ? "Share record" : "Share layer"}
        >
          <ShareIcon sx={{ fontSize: size === "small" ? "1.1rem" : "1.35rem" }} />
        </IconButton>
      </Tooltip>

      {/* Top-Right Corner Modal with Backdrop */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 250,
            sx: {
              backgroundColor: "rgba(15, 23, 42, 0.65)",
              backdropFilter: "blur(3px)",
              zIndex: 1399
            }
          }
        }}
        sx={{
          zIndex: 1400,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end"
        }}
      >
        <Fade in={open} timeout={250}>
          <Box
            sx={{
              position: "fixed",
              top: "calc(4.1rem + 0.6rem)", // Positioned just below the navbar (4.1rem)
              right: "1rem",
              width: 500,
              maxWidth: "calc(100vw - 2rem)",
              bgcolor: "#475467",
              color: "#FCFCFD",
              borderRadius: "0.75rem",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.4)",
              overflow: "hidden",
              outline: "none",
              zIndex: 1401
            }}
          >
            {/* ── Modal Header ── */}
            <Box
              sx={{
                px: 10,
                pt: 10,
                pb: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "rgba(255, 255, 255, 0.02)"
              }}
            >
              <Box sx={{ minWidth: 0, pr: 1 }}>
                <Typography
                  variant='subtitle1'
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: "#FFFFFF",
                    fontSize: "1rem",
                    letterSpacing: 0.1
                  }}
                >
                  {isRecordExport ? "Share Record" : "Share Layer"}
                  <ShareIcon sx={{ fontSize: "1rem", verticalAlign: "middle", mr: 0.5 }} />
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    color: "rgba(252, 252, 253, 0.6)",
                    display: "block",
                    mt: 0.35,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                  title={displayName}
                >
                  {displayName}
                </Typography>
              </Box>

              <IconButton
                size='small'
                onClick={() => setOpen(false)}
                sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  bgcolor: "rgba(255, 255, 255, 0.05)",
                  p: 0.75,
                  "&:hover": {
                    color: "#FFFFFF",
                    bgcolor: "rgba(255, 255, 255, 0.15)"
                  }
                }}
                aria-label='Close share dialog'
              >
                <CloseIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </Box>

            <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }} />

            {/* ── Export sections (§10.1) ──────────────────────────────────
                Live first, full width, then Offline below — each section's
                Raw/Styled pair sits side by side (horizontal), not stacked in
                a shared column with the other section. The old 2x2 grid put
                Offline and Live in two ~180px columns — a full-width section
                gives every control the room its content actually needs. */}
            <Box sx={{ px: 10, py: 10 }}>
              {/* ── Live section ── */}
              <ColumnHeading title='Live' subtitle='CultureMap HTTPS' />

              {/* GeoParquet vs GeoPackage — shared by both Live cells, vectors only. */}
              {offerGpkg && (
                <Stack direction='row' spacing={8} sx={{ mt: 8, alignItems: "center" }}>
                  <Typography variant='caption' sx={{ color: "rgba(252,252,253,0.6)", flexShrink: 0, fontWeight: 600, letterSpacing: 0.2 }}>
                    Format
                  </Typography>
                  <Select
                    size='small'
                    fullWidth
                    value={effectiveVectorSource}
                    onChange={(e) => setVectorSource(e.target.value as VectorSource)}
                    inputProps={{ "aria-label": "Live layer format" }}
                    // The Select's popup Menu portals to <body> with the theme's
                    // default zIndex (1300); this Modal sits at 1400/1401, so
                    // without this the menu would render behind the backdrop.
                    MenuProps={{ sx: { zIndex: 1410 } }}
                    sx={{
                      fontSize: "0.75rem",
                      color: "#FCFCFD",
                      bgcolor: "rgba(255,255,255,0.03)",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.12)" },
                      "& .MuiSvgIcon-root": { color: "rgba(252,252,253,0.6)" }
                    }}
                  >
                    <MenuItem value='geoparquet' sx={{ fontSize: "0.8rem" }}>
                      GeoParquet
                    </MenuItem>
                    <MenuItem value='gpkg' sx={{ fontSize: "0.8rem" }}>
                      GeoPackage (for QGIS on macOS)
                    </MenuItem>
                  </Select>
                </Stack>
              )}

              {/* Live actions — Raw and Styled side by side */}
              <Stack direction='row' spacing={8} sx={{ mt: 8 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <RowLabel title='Raw' subtitle='data only' />
                  <Box sx={{ mt: 0.5 }}>
                    <MatrixCell
                      label={copied ? "Copied" : "Copy data URL"}
                      description='Data only — no styling'
                      icon={
                        isCopying ? (
                          <CircularProgress size={16} thickness={4} sx={{ color: "#34D399" }} />
                        ) : copied ? (
                          <CheckIcon sx={{ fontSize: "1.1rem", color: "#4ADE80" }} />
                        ) : (
                          <ContentCopyIcon sx={{ fontSize: "1.1rem", color: "#34D399" }} />
                        )
                      }
                      onClick={() => void handleCopyLink()}
                      disabled={busy || !live.ok}
                      disabledReason={live.ok ? null : live.reason}
                    />
                  </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <RowLabel title='Styled' subtitle='QGIS .qlr' />
                  <Box sx={{ mt: 0.5 }}>
                    <MatrixCell
                      label='QGIS live layer'
                      description='.qlr on a signed link'
                      icon={
                        isDownloading ? (
                          <CircularProgress size={16} thickness={4} sx={{ color: "#60A5FA" }} />
                        ) : (
                          <FileDownloadOutlinedIcon sx={{ fontSize: "1.1rem", color: "#60A5FA" }} />
                        )
                      }
                      onClick={() => void handleDownloadQlr()}
                      disabled={busy || !live.ok}
                      disabledReason={live.ok ? null : live.reason}
                    />
                  </Box>
                </Box>
              </Stack>

              <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)", my: 10 }} />

              {/* ── Offline section ── */}
              <ColumnHeading title='Offline' subtitle='you keep the file' />
              <Stack direction='row' spacing={8} sx={{ mt: 8 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <RowLabel title='Raw' subtitle='data only' />
                  <Box sx={{ mt: 0.5 }}>
                    <MatrixCell
                      label={rawOffline.label}
                      description={rawOffline.description}
                      icon={
                        isDownloadingRaw ? (
                          <CircularProgress size={16} thickness={4} sx={{ color: "#C084FC" }} />
                        ) : (
                          <CloudDownloadOutlinedIcon sx={{ fontSize: "1.1rem", color: "#C084FC" }} />
                        )
                      }
                      onClick={rawOffline.onClick}
                      disabled={busy}
                      disabledReason={null}
                    />
                  </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <RowLabel title='Styled' subtitle='QGIS .qml' />
                  <Box sx={{ mt: 0.5 }}>
                    <MatrixCell
                      label={styledOffline.label}
                      description={styledOffline.description}
                      icon={
                        isDownloadingStyle ? (
                          <CircularProgress size={16} thickness={4} sx={{ color: "#C084FC" }} />
                        ) : (
                          <FileDownloadOutlinedIcon sx={{ fontSize: "1.1rem", color: "#C084FC" }} />
                        )
                      }
                      onClick={styledOffline.onClick}
                      disabled={busy || styledOffline.disabledReason !== null}
                      disabledReason={styledOffline.disabledReason}
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }} />

            {/* ── Lifetime / auth caption for the Live column (§10.1) ── */}
            <Box sx={{ px: 10, py: 10, display: "flex", gap: 10, alignItems: "center", bgcolor: "rgba(0, 0, 0, 0.2)" }}>
              <AccessTimeIcon sx={{ fontSize: "0.95rem", color: "rgba(252, 252, 253, 0.45)", flexShrink: 0 }} />
              <Typography variant='caption' sx={{ color: "rgba(252, 252, 253, 0.55)", lineHeight: 1.4, fontSize: "0.75rem" }}>
                {probingLive ? "Checking what this dataset supports…" : expiryNotice}
                {/* Spatial only: the plugin doesn't read CM-tables yet, so for
                    tables this would promise something it can't deliver. */}
                {!probingLive &&
                  shareContext.kind === "spatial-dataset" &&
                  " Need a layer that doesn't expire? Use the CultureMap QGIS plugin."}
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

// ─── Matrix sub-components ────────────────────────────────────────────────────

const ColumnHeading: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <Box sx={{ px: 0.25 }}>
    <Typography
      variant='caption'
      sx={{
        display: "block",
        color: "rgba(252,252,253,0.85)",
        fontWeight: 700,
        fontSize: "0.7rem",
        letterSpacing: 0.4,
        textTransform: "uppercase"
      }}
    >
      {title}
    </Typography>
    <Typography variant='caption' sx={{ display: "block", color: "rgba(252,252,253,0.4)", fontSize: "0.65rem", lineHeight: 1.2 }}>
      {subtitle}
    </Typography>
  </Box>
);

const RowLabel: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", pr: 0.5 }}>
    <Typography variant='caption' sx={{ color: "rgba(252,252,253,0.85)", fontWeight: 700, fontSize: "0.7rem", letterSpacing: 0.3 }}>
      {title}
    </Typography>
    <Typography variant='caption' sx={{ color: "rgba(252,252,253,0.4)", fontSize: "0.62rem", lineHeight: 1.2 }}>
      {subtitle}
    </Typography>
  </Box>
);

interface MatrixCellProps {
  label: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /**
   * Why this cell cannot be used. Rendered in place of the description so the
   * cell stays visible and self-explanatory — §10.1 is explicit that disabled
   * cells are greyed, never hidden.
   */
  disabledReason?: string | null;
}

const MatrixCell: React.FC<MatrixCellProps> = ({ label, description, icon, onClick, disabled, disabledReason }) => {
  const unavailable = Boolean(disabledReason);
  const cell = (
    <Box
      component='button'
      onClick={onClick}
      disabled={disabled}
      aria-label={unavailable ? `${label} — unavailable. ${disabledReason}` : label}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        p: 1,
        borderRadius: "0.5rem",
        bgcolor: unavailable ? "rgba(255,255,255,0.015)" : "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: unavailable ? 0.45 : disabled ? 0.6 : 1,
        transition: "all 0.15s ease-in-out",
        "&:not(:disabled):hover": {
          bgcolor: "rgba(255,255,255,0.08)",
          borderColor: "rgba(255,255,255,0.16)",
          transform: "translateY(-1px)"
        },
        "&:not(:disabled):active": { transform: "translateY(0)" }
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box sx={{ display: "flex", flexShrink: 0 }}>{icon}</Box>
        <Typography variant='body2' sx={{ color: "#FCFCFD", fontWeight: 600, fontSize: "0.8rem", lineHeight: 1.25 }}>
          {label}
        </Typography>
      </Box>
      <Typography
        variant='caption'
        sx={{
          color: unavailable ? "rgba(252,252,253,0.5)" : "rgba(252,252,253,0.55)",
          fontSize: "0.68rem",
          lineHeight: 1.3,
          textAlign: "left"
        }}
      >
        {disabledReason ?? description}
      </Typography>
    </Box>
  );

  // Tooltip repeats the reason: the inline caption is clamped by the cell width.
  return unavailable ? (
    <Tooltip title={disabledReason ?? ""}>
      <Box sx={{ display: "flex" }}>{cell}</Box>
    </Tooltip>
  ) : (
    cell
  );
};

export default LayerSharePopover;
